// src/interfaces/manager/DatabaseManager.js
import React, { useState, useEffect } from 'react';
import DataTable from '../../components/admin/DataTable';
import RecordModal from '../../components/admin/RecordModal';
import { jobTypesTableConfig, transformJobTypeForApi, transformJobTypeFromApi } from '../../config/database/sfJobTypesConfig';
import { paperStocksTableConfig, transformPaperStockForApi, transformPaperStockFromApi } from '../../config/database/sfPaperStocksConfig';
import { finishingProcessesTableConfig, transformFinishingProcessForApi, transformFinishingProcessFromApi } from '../../config/database/sfFinishingProcessesConfig';
import { finishingMaterialsTableConfig, transformFinishingMaterialForApi, transformFinishingMaterialFromApi, validateFinishingMaterial } from '../../config/database/sfFinishingMaterialsConfig';
import { pricingConstantsTableConfig, transformPricingConstantForApi, transformPricingConstantFromApi, validatePricingConstant } from '../../config/database/pricingConstantsConfig';
import { lfFinishingProcessesTableConfig, transformLfFinishingProcessForApi, transformLfFinishingProcessFromApi, validateLfFinishingProcess } from '../../config/database/lfFinishingProcessesConfig';
import { lfJobTypesTableConfig, transformLfJobTypeForApi, transformLfJobTypeFromApi, validateLfJobType } from '../../config/database/lfJobTypesConfig';
import { lfMaterialsTableConfig, transformLfMaterialForApi, transformLfMaterialFromApi, validateLfMaterial } from '../../config/database/lfMaterialsConfig';
import { lfRollerBannersTableConfig, transformLfRollerBannerForApi, transformLfRollerBannerFromApi, validateLfRollerBanner } from '../../config/database/lfRollerBannersConfig';
import { printlogicCustomerSyncLogTableConfig } from '../../config/database/printlogicCustomerSyncLogConfig';
const API_BASE = process.env.REACT_APP_API_URL || '';
const DatabaseManager = () => {
  const [selectedTable, setSelectedTable] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [error, setError] = useState(null);
  const [finishingProcesses, setFinishingProcesses] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [globalMultiplierStatus, setGlobalMultiplierStatus] = useState(null);
  const [globalMultiplierPercent, setGlobalMultiplierPercent] = useState('5');
  const [globalMultiplierReason, setGlobalMultiplierReason] = useState('');
  const [globalMultiplierLoading, setGlobalMultiplierLoading] = useState(false);
  const [globalMultiplierSubmitting, setGlobalMultiplierSubmitting] = useState(false);
  const [globalMultiplierError, setGlobalMultiplierError] = useState(null);

  // Available tables configuration
  const availableTables = [
    { id: 'sf_job_types', name: 'Small Format Job Types', category: 'Small Format', config: jobTypesTableConfig },
    { id: 'sf_paper_stock', name: 'Paper Stocks', category: 'Small Format', config: paperStocksTableConfig },
    { id: 'sf_finishing_processes', name: 'Finishing Processes', category: 'Small Format', config: finishingProcessesTableConfig },
    { id: 'sf_finishing_materials', name: 'Finishing Materials', category: 'Small Format', config: finishingMaterialsTableConfig },
    { id: 'sf_printers', name: 'Printers', category: 'Small Format', config: null },
    { id: 'pricing_constants', name: 'Pricing Constants', category: 'Configuration', config: pricingConstantsTableConfig },
    { id: 'lf_finishing_processes', name: 'Large Format Finishing Processes', category: 'Large Format', config: lfFinishingProcessesTableConfig },
    { id: 'lf_job_types', name: 'Large Format Job Types', category: 'Large Format', config: lfJobTypesTableConfig },
    { id: 'lf_materials', name: 'Large Format Materials', category: 'Large Format', config: lfMaterialsTableConfig },
    { id: 'lf_roller_banners', name: 'Roller Banners', category: 'Large Format', config: lfRollerBannersTableConfig },
    { id: 'lf_print_machines', name: 'Print Machines', category: 'Large Format', config: null },
    { id: 'printlogic_customer_sync_log', name: 'Printlogic Customer Sync Log', category: 'Configuration', config: printlogicCustomerSyncLogTableConfig },
  ];

  // Group tables by category for better organization
  const groupedTables = availableTables.reduce((acc, table) => {
    if (!acc[table.category]) {
      acc[table.category] = [];
    }
    acc[table.category].push(table);
    return acc;
  }, {});

  // Get current table configuration
  const currentTableConfig = availableTables.find(t => t.id === selectedTable)?.config;

  // Load data when table selection changes
  useEffect(() => {
    if (selectedTable && currentTableConfig) {
      loadTableData();
      
      // Load additional data for finishing materials
      if (selectedTable === 'sf_finishing_materials') {
        loadFinishingProcesses();
        loadMaterialTypes();
      }
      
      // Load finishing processes for job types (to populate default_finishing options)
      if (selectedTable === 'sf_job_types') {
        loadFinishingProcesses();
      }

      if (selectedTable === 'pricing_constants') {
        loadGlobalMultiplierStatus();
      }
    } else {
      setData([]);
      setGlobalMultiplierStatus(null);
      setGlobalMultiplierError(null);
    }
  }, [selectedTable]);

  const loadGlobalMultiplierStatus = async () => {
    setGlobalMultiplierLoading(true);
    setGlobalMultiplierError(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/pricing-constants/global-price-multiplier`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || 'Failed to load global multiplier data');
      }

      const result = await response.json();
      setGlobalMultiplierStatus(result);
    } catch (err) {
      console.error('Error loading global multiplier status:', err);
      setGlobalMultiplierError(err.message);
      setGlobalMultiplierStatus(null);
    } finally {
      setGlobalMultiplierLoading(false);
    }
  };

  const loadTableData = async () => {
    if (!currentTableConfig) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}${currentTableConfig.apiEndpoint}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load data: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Handle different response formats
      if (Array.isArray(result)) {
        setData(result);
      } else if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else {
        throw new Error('Invalid response format');
      }
      
    } catch (err) {
      console.error('Error loading table data:', err);
      setError(err.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFinishingProcesses = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/small-format/finishing-materials/processes`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const processes = await response.json();
        setFinishingProcesses(processes);
        
        // Update the config options
        if (currentTableConfig && currentTableConfig.fields) {
          const processField = currentTableConfig.fields.find(f => f.key === 'finish_process_id');
          if (processField) {
            processField.options = processes.map(p => ({
              value: p.id,
              label: `${p.name} (${p.category || 'General'})`
            }));
          }
          
          // Also update default_finishing field options for job types
          const defaultFinishingField = currentTableConfig.fields.find(f => f.key === 'default_finishing');
          if (defaultFinishingField) {
            defaultFinishingField.options = processes.map(p => ({
              value: p.id,
              label: `${p.name} (${p.category || 'General'})`
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error loading finishing processes:', err);
    }
  };

  const loadMaterialTypes = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/small-format/finishing-materials/types`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const types = await response.json();
        setMaterialTypes(types);
        
        // Update the config options
        if (currentTableConfig && currentTableConfig.fields) {
          const typeField = currentTableConfig.fields.find(f => f.key === 'material_type');
          if (typeField) {
            typeField.options = [
              ...types.map(type => ({ value: type, label: type })),
              { value: 'paper', label: 'Paper' },
              { value: 'ink', label: 'Ink' },
              { value: 'binding', label: 'Binding' },
              { value: 'laminate', label: 'Laminate' },
              { value: 'adhesive', label: 'Adhesive' },
              { value: 'other', label: 'Other' }
            ];
          }
        }
      }
    } catch (err) {
      console.error('Error loading material types:', err);
    }
  };

  const handleEdit = (record) => {
    if (!currentTableConfig) return;

    if (
      selectedTable === 'pricing_constants' &&
      record?.scope === 'global' &&
      record?.name === 'global_price_multiplier'
    ) {
      setError('global_price_multiplier is locked. Use the cumulative increase panel above the table.');
      return;
    }

    console.log('Raw record before transformation:', record);
    // Transform data based on table type
    let transformedRecord = record;
    
    switch (selectedTable) {
      case 'sf_job_types':
        transformedRecord = transformJobTypeFromApi(record);
        break;
      case 'sf_paper_stock':
        transformedRecord = transformPaperStockFromApi(record);
        break;
      case 'sf_finishing_processes':
        transformedRecord = transformFinishingProcessFromApi(record);
        break;
      case 'sf_finishing_materials':
        transformedRecord = transformFinishingMaterialFromApi(record);
        break;
      case 'pricing_constants':
        transformedRecord = transformPricingConstantFromApi(record);
        break;
      case 'lf_finishing_processes':
        transformedRecord = transformLfFinishingProcessFromApi(record);
        break;
      case 'lf_job_types':
        transformedRecord = transformLfJobTypeFromApi(record);
        break;
      case 'lf_materials':
        transformedRecord = transformLfMaterialFromApi(record);
        break;
      case 'lf_roller_banners':
        transformedRecord = transformLfRollerBannerFromApi(record);
        break;
      default:
        break;
    }
    
    setSelectedRecord(transformedRecord);
    setModalOpen(true);
  };

  const handleSave = async (formData) => {
    if (!currentTableConfig) return;
    
    try {
      // Transform data based on table type
      let apiData = formData;
      
      switch (selectedTable) {
        case 'sf_job_types':
          apiData = transformJobTypeForApi(formData);
          break;
        case 'sf_paper_stock':
          apiData = transformPaperStockForApi(formData);
          break;
        case 'sf_finishing_processes':
          apiData = transformFinishingProcessForApi(formData);
          break;
        case 'sf_finishing_materials':
          // Validate finishing materials
          const validationErrors = validateFinishingMaterial(formData);
          if (validationErrors) {
            throw new Error(`Validation failed: ${Object.values(validationErrors).join(', ')}`);
          }
          apiData = transformFinishingMaterialForApi(formData);
          break;
        case 'pricing_constants':
          // Validate pricing constants
          const constantValidationErrors = validatePricingConstant(formData);
          if (constantValidationErrors) {
            throw new Error(`Validation failed: ${Object.values(constantValidationErrors).join(', ')}`);
          }
          apiData = transformPricingConstantForApi(formData);

          if (
            apiData?.scope === 'global' &&
            apiData?.name === 'global_price_multiplier'
          ) {
            throw new Error('global_price_multiplier is managed via cumulative increase only.');
          }
          break;
        case 'lf_finishing_processes':
          // Validate LF finishing processes
          const lfValidationErrors = validateLfFinishingProcess(formData);
          if (lfValidationErrors) {
            throw new Error(`Validation failed: ${Object.values(lfValidationErrors).join(', ')}`);
          }
          apiData = transformLfFinishingProcessForApi(formData);
          break;
        case 'lf_job_types':
          // Validate LF job types
          const lfJobTypeValidationErrors = validateLfJobType(formData);
          if (lfJobTypeValidationErrors) {
            throw new Error(`Validation failed: ${Object.values(lfJobTypeValidationErrors).join(', ')}`);
          }
          apiData = transformLfJobTypeForApi(formData);
          break;
        case 'lf_materials':
          // Validate LF materials
          const lfMaterialValidationErrors = validateLfMaterial(formData);
          if (lfMaterialValidationErrors) {
            throw new Error(`Validation failed: ${Object.values(lfMaterialValidationErrors).join(', ')}`);
          }
          apiData = transformLfMaterialForApi(formData);
          break;
        case 'lf_roller_banners':
          // Validate LF roller banners
          const lfRollerBannerValidationErrors = validateLfRollerBanner(formData);
          if (lfRollerBannerValidationErrors) {
            throw new Error(`Validation failed: ${Object.values(lfRollerBannerValidationErrors).join(', ')}`);
          }
          apiData = transformLfRollerBannerForApi(formData);
          break;
        default:
          break;
      }
      console.log('selectedRecord:', selectedRecord);
      console.log('selectedRecord.id:', selectedRecord?.id);
      console.log('isUpdate will be:', selectedRecord && selectedRecord.id);
      const isUpdate = selectedRecord && selectedRecord.id;
      const url = isUpdate 
        ? `${currentTableConfig.apiEndpoint}/${selectedRecord.id}`
        : currentTableConfig.apiEndpoint;
      
      const response = await fetch(`${API_BASE}${url}`, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(apiData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save record');
      }
      
      // Reload data
      await loadTableData();
      
      // Close modal
      setModalOpen(false);
      setSelectedRecord(null);
      
    } catch (err) {
      console.error('Error saving record:', err);
      throw err; // Re-throw to be handled by the modal
    }
  };

  const handleApplyGlobalMultiplierIncrease = async () => {
    const parsedPercent = Number(globalMultiplierPercent);
    if (Number.isNaN(parsedPercent)) {
      setGlobalMultiplierError('Increase percentage must be a valid number');
      return;
    }

    const currentMultiplier = Number(globalMultiplierStatus?.current_multiplier || 1);
    const projectedMultiplier = currentMultiplier * (1 + parsedPercent / 100);

    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(
      `Apply ${parsedPercent}% increase?\n\nCurrent multiplier: ${currentMultiplier.toFixed(6)}\nProjected multiplier: ${projectedMultiplier.toFixed(6)}`
    );
    if (!confirmed) {
      return;
    }

    setGlobalMultiplierSubmitting(true);
    setGlobalMultiplierError(null);

    try {
      await submitGlobalMultiplierAdjustment(parsedPercent, globalMultiplierReason);
      setGlobalMultiplierReason('');
    } catch (err) {
      console.error('Error applying global multiplier increase:', err);
      setGlobalMultiplierError(err.message);
    } finally {
      setGlobalMultiplierSubmitting(false);
    }
  };

  const submitGlobalMultiplierAdjustment = async (percentIncrease, reason) => {
    const response = await fetch(`${API_BASE}/api/admin/pricing-constants/global-price-multiplier/adjust`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        percent_increase: percentIncrease,
        reason,
      }),
    });

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => ({}));
      throw new Error(errorPayload.error || 'Failed to apply multiplier increase');
    }

    await Promise.all([loadTableData(), loadGlobalMultiplierStatus()]);
  };

  const handleRollbackLastIncrease = async () => {
    const history = globalMultiplierStatus?.history || [];
    const lastIncrease = history.find((entry) => Number(entry.percent_change) > 0);

    if (!lastIncrease) {
      setGlobalMultiplierError('No previous increase found to roll back.');
      return;
    }

    const oldMultiplier = Number(lastIncrease.old_multiplier);
    const newMultiplier = Number(lastIncrease.new_multiplier);
    if (!newMultiplier || Number.isNaN(oldMultiplier) || Number.isNaN(newMultiplier)) {
      setGlobalMultiplierError('Could not calculate rollback value from history entry.');
      return;
    }

    const rollbackPercent = ((oldMultiplier / newMultiplier) - 1) * 100;
    const currentMultiplier = Number(globalMultiplierStatus?.current_multiplier || 1);
    const projectedMultiplier = currentMultiplier * (1 + rollbackPercent / 100);

    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(
      `Rollback last increase of ${Number(lastIncrease.percent_change).toFixed(2)}% from ${lastIncrease.changed_at}?\n\nCurrent multiplier: ${currentMultiplier.toFixed(6)}\nRollback change: ${rollbackPercent.toFixed(4)}%\nProjected multiplier: ${projectedMultiplier.toFixed(6)}`
    );
    if (!confirmed) {
      return;
    }

    setGlobalMultiplierSubmitting(true);
    setGlobalMultiplierError(null);

    try {
      const rollbackReason = `Rollback of +${Number(lastIncrease.percent_change).toFixed(2)}% from ${lastIncrease.changed_at}`;
      await submitGlobalMultiplierAdjustment(rollbackPercent, rollbackReason);
    } catch (err) {
      console.error('Error rolling back last increase:', err);
      setGlobalMultiplierError(err.message);
    } finally {
      setGlobalMultiplierSubmitting(false);
    }
  };

  const handleDelete = async (record) => {
    if (!currentTableConfig) return;
    
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Are you sure you want to delete "${record.name || record.material_name || record.id}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}${currentTableConfig.apiEndpoint}/${record.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete record');
      }
      
      // Reload data
      await loadTableData();
      
    } catch (err) {
      console.error('Error deleting record:', err);
      setError(err.message);
    }
  };

  const handleAdd = () => {
    setSelectedRecord(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRecord(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Database Manager
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Manage pricing database tables directly through the web interface.
          </p>
        </div>
      </div>

      {/* Table Selection */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <label htmlFor="table-select" className="block text-sm font-medium text-gray-700 mb-2">
            Select Table
          </label>
          <select
            id="table-select"
            value={selectedTable}
            onChange={(e) => setSelectedTable(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">Choose a table...</option>
            {Object.entries(groupedTables).map(([category, tables]) => (
              <optgroup key={category} label={category}>
                {tables.map((table) => (
                  <option 
                    key={table.id} 
                    value={table.id}
                    disabled={!table.config}
                  >
                    {table.name} {!table.config && '(Not Available)'}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>

      {/* Data Table */}
      {selectedTable && currentTableConfig && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-medium text-gray-900">
                {currentTableConfig.name}
              </h4>
              {!currentTableConfig.readOnly && (
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Add New Record
                </button>
              )}
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                Error: {error}
              </div>
            )}

            {selectedTable === 'pricing_constants' && (
              <div className="mb-6 border border-indigo-200 rounded-lg bg-indigo-50 p-4">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <h5 className="text-base font-semibold text-indigo-900">Global Price Multiplier</h5>
                    <p className="text-sm text-indigo-800 mt-1">
                      Apply cumulative price changes. Manual edit for this constant is disabled.
                    </p>
                    <div className="mt-3 text-sm text-indigo-900">
                      <span className="font-medium">Current multiplier:</span>{' '}
                      {globalMultiplierLoading
                        ? 'Loading...'
                        : Number(globalMultiplierStatus?.current_multiplier || 1).toFixed(6)}
                    </div>
                    <div className="text-sm text-indigo-900">
                      <span className="font-medium">Effective uplift vs baseline:</span>{' '}
                      {globalMultiplierLoading
                        ? 'Loading...'
                        : `${(((Number(globalMultiplierStatus?.current_multiplier || 1) - 1) * 100).toFixed(2))}%`}
                    </div>
                    {!globalMultiplierStatus?.exists && !globalMultiplierLoading && (
                      <div className="mt-2 text-xs text-indigo-700">
                        The constant does not exist yet. It will be created automatically on first apply.
                      </div>
                    )}
                  </div>

                  <div className="w-full md:w-auto md:min-w-[380px]">
                    <div className="grid grid-cols-1 gap-2">
                      <input
                        type="number"
                        step="0.01"
                        value={globalMultiplierPercent}
                        onChange={(e) => setGlobalMultiplierPercent(e.target.value)}
                        placeholder="Increase percent (e.g. 5)"
                        className="w-full px-3 py-2 border border-indigo-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        disabled={globalMultiplierSubmitting || globalMultiplierLoading}
                      />
                      <input
                        type="text"
                        value={globalMultiplierReason}
                        onChange={(e) => setGlobalMultiplierReason(e.target.value)}
                        placeholder="Reason (optional)"
                        className="w-full px-3 py-2 border border-indigo-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        disabled={globalMultiplierSubmitting || globalMultiplierLoading}
                      />
                      <button
                        type="button"
                        onClick={handleApplyGlobalMultiplierIncrease}
                        disabled={globalMultiplierSubmitting || globalMultiplierLoading}
                        className="inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {globalMultiplierSubmitting ? 'Applying...' : 'Apply Cumulative Increase'}
                      </button>
                      <button
                        type="button"
                        onClick={handleRollbackLastIncrease}
                        disabled={globalMultiplierSubmitting || globalMultiplierLoading || !(globalMultiplierStatus?.history || []).some((entry) => Number(entry.percent_change) > 0)}
                        className="inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 disabled:opacity-50"
                      >
                        Rollback Last Increase
                      </button>
                    </div>
                  </div>
                </div>

                {globalMultiplierError && (
                  <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                    Error: {globalMultiplierError}
                  </div>
                )}

                <div className="mt-4">
                  <h6 className="text-sm font-semibold text-indigo-900 mb-2">Change History</h6>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-indigo-100 rounded-md">
                      <thead className="bg-indigo-100 text-indigo-900 text-xs uppercase">
                        <tr>
                          <th className="px-3 py-2 text-left">When</th>
                          <th className="px-3 py-2 text-left">User</th>
                          <th className="px-3 py-2 text-right">Change %</th>
                          <th className="px-3 py-2 text-right">Old</th>
                          <th className="px-3 py-2 text-right">New</th>
                          <th className="px-3 py-2 text-left">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm text-gray-700">
                        {(globalMultiplierStatus?.history || []).length === 0 ? (
                          <tr>
                            <td className="px-3 py-3 text-center" colSpan={6}>No changes recorded yet.</td>
                          </tr>
                        ) : (
                          (globalMultiplierStatus.history || []).map((entry) => (
                            <tr key={entry.id} className="border-t border-indigo-50">
                              <td className="px-3 py-2 whitespace-nowrap">{entry.changed_at || '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap">{entry.changed_by || 'unknown'}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">{Number(entry.percent_change).toFixed(2)}%</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">{Number(entry.old_multiplier).toFixed(6)}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">{Number(entry.new_multiplier).toFixed(6)}</td>
                              <td className="px-3 py-2">{entry.reason || '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <DataTable
              data={data}
              columns={currentTableConfig.columns}
              loading={loading}
              onEdit={currentTableConfig.readOnly ? undefined : handleEdit}
              onDelete={currentTableConfig.readOnly ? undefined : handleDelete}
              searchPlaceholder={`Search ${currentTableConfig.name.toLowerCase()}...`}
            />
          </div>
        </div>
      )}

      {/* Record Modal */}
      {modalOpen && currentTableConfig && (
        <RecordModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          fields={currentTableConfig.fields}
          record={selectedRecord}
          title={selectedRecord ? `Edit ${currentTableConfig.name}` : `Add New ${currentTableConfig.name}`}
        />
      )}
    </div>
  );
};

export default DatabaseManager;
